/**
 * The vocabulary every pose is written in, and the standing, moving and lying base poses
 * (05 §6.2): no keyframes, a gait is a few phase-shifted sines. One-shots and the ways a man
 * goes down build on these (poseActions.ts, poseStates.ts); poses.ts chooses between them.
 */

import type { Bone } from 'three';
import { B, REST } from './rig';

export type Motion =
  | 'idle' | 'walk' | 'run' | 'crawl' | 'aim' | 'dead'
  /** Carbine recoil, standing and prone: the braced stance plus a kick timed from `age`. */
  | 'fire' | 'fireProne'
  /** One-shots, played from `age` = 0: grenade or smoke throw, knife thrust, hit stagger. */
  | 'throw' | 'stab' | 'hit'
  /** Going down, played from `age` = 0 and ending in the `dead` corpse: shot, or thrown back by a blast. */
  | 'deathCrumple' | 'deathBlast'
  /** Out of the fight but alive: down on one side, clutching, knees working. */
  | 'wounded'
  /** On his knees with his hands up, rifle on the ground: the ranger taken prisoner. */
  | 'surrender'
  /** Climbing the rope ladder; `age` drives the rungs. */
  | 'board'
  /** ROUND7, sneaking: the crouched walk, the crouch standing still, and crouched behind the raised rifle. */
  | 'sneak' | 'crouch' | 'crouchAim';

export interface PoseInput {
  motion: Motion;
  /** Metres travelled: drives the gait so feet do not slide. */
  travelled: number;
  /** Upper-body turn toward the aim, radians, already clamped to what a spine can do. */
  twist: number;
  /** Walking backwards: the gait runs in reverse. */
  reverse: boolean;
  /** Seconds since the current one-shot began (or since the last shot, for `fire`). Default 0. */
  age?: number;
  /** Recoil laid over a moving pose, 0..1: a man firing on the walk. `fire` works its own out. */
  kick?: number;
  /** Hit stagger laid over a moving pose, 0..1. `hit` works its own out. */
  flinch?: number;
  /** Arms raised over whatever the legs are doing: the captured officer walks like this. */
  handsUp?: boolean;
  /** How hard a looping pose works, 0..1: a stabilised wounded man lies quiet. Default 1. */
  vigour?: number;
}

export type BoneOf = (index: number) => Bone;

/** The skeleton as the pose functions see it. */
export interface Rig {
  bones: readonly Bone[];
  bone: BoneOf;
}

export const PELVIS_HEIGHT = 0.88;

/** Limb poses move "on twos" (05 §6.2): one-shot clocks are read on this grid. */
export const POSE_STEP_HZ = 12;
export const onTwos = (seconds: number): number => Math.floor(seconds * POSE_STEP_HZ + 1e-6) / POSE_STEP_HZ;

/**
 * Progress 0..1 through a one-shot `seconds` long, read on the pose grid - and exactly 1 once the
 * time is up. Without that last clause a length that is not a whole number of pose steps (0.8 s is
 * 9.6 of them) ends a step short of its final drawing, and the hand-over to the pose that follows
 * (the corpse, the carry) shows as a jump.
 */
export const steppedPhase = (age: number, seconds: number): number =>
  age >= seconds ? 1 : Math.min(1, onTwos(age) / seconds);

/**
 * Every bone back to its rest offset with no rotation, in the usual Euler order (a crouch changes the chest's):
 * what each pose starts from.
 */
export function resetPose(bones: readonly Bone[]): void {
  for (let i = 0; i < bones.length; i++) {
    const b = bones[i];
    const rest = REST[i];
    if (!b || !rest) continue;
    b.rotation.set(0, 0, 0, 'XYZ');
    b.position.set(rest[1], rest[2], rest[3]);
  }
}

/** Rifle held across the chest, muzzle forward and a little down: the default carry. */
export function carry(bone: BoneOf): void {
  bone(B.lUpperArm).rotation.set(-0.75, 0, -0.35);
  bone(B.lForeArm).rotation.set(-1.25, 0, 0);
  bone(B.rUpperArm).rotation.set(-0.35, 0, 0.3);
  bone(B.rForeArm).rotation.set(-1.45, 0, 0);
  bone(B.weapon).position.set(-0.1, 0.2, 0.2);
  bone(B.weapon).rotation.set(0.12, 0.32, 0);
}

export function shouldered(bone: BoneOf): void {
  bone(B.lUpperArm).rotation.set(-1.35, 0, -0.45);
  bone(B.lForeArm).rotation.set(-0.35, 0, 0);
  bone(B.rUpperArm).rotation.set(-1.05, 0, 0.5);
  bone(B.rForeArm).rotation.set(-1.2, 0, 0);
  bone(B.weapon).position.set(-0.14, 0.36, 0.26);
  bone(B.weapon).rotation.set(0, 0, 0);
}

/** Rifle up, knees bent, weight forward: the braced stance of a man aiming or firing. */
export function aimStance(bone: BoneOf): void {
  shouldered(bone);
  bone(B.lThigh).rotation.x = -0.35;
  bone(B.lShin).rotation.x = 0.5;
  bone(B.rThigh).rotation.x = 0.25;
  bone(B.rShin).rotation.x = 0.3;
  bone(B.pelvis).position.y = 0.8;
  bone(B.spine).rotation.x = 0.16;
}

export function locomotion(bone: BoneOf, input: PoseInput): void {
  const run = input.motion === 'run';
  const dir = input.reverse ? -1 : 1;
  // One full cycle (two steps) per stride length, so the gait tracks ground speed.
  const phase = (input.travelled / (run ? 2.3 : input.reverse ? 1.1 : 1.45)) * Math.PI * 2 * dir;
  const swing = run ? 0.95 : input.reverse ? 0.4 : 0.55;
  const knee = run ? 1.5 : 0.85;
  const sin = Math.sin(phase);
  const cos = Math.cos(phase);
  bone(B.lThigh).rotation.x = -swing * sin;
  bone(B.rThigh).rotation.x = swing * sin;
  bone(B.lShin).rotation.x = knee * Math.max(0, cos * dir) + 0.08;
  bone(B.rShin).rotation.x = knee * Math.max(0, -cos * dir) + 0.08;
  bone(B.pelvis).position.y = PELVIS_HEIGHT - (run ? 0.07 : 0.03) * Math.abs(sin);
  // Leaning into a run, upright on a walk, leaning back slightly when backing away.
  bone(B.spine).rotation.x = run ? 0.3 : input.reverse ? -0.05 : 0.07;
  bone(B.spine).rotation.y = 0.1 * sin;
  bone(B.head).rotation.x = run ? -0.2 : -0.04;
}

/**
 * Low crawl. With the pelvis tipped forward the body's axes are: local +y toward the head
 * (forward along the ground), local +z into the ground. So limbs move by rotating about
 * local z - sweeping across the ground - never about x, which would drive them into it.
 */
export function crawl(bone: BoneOf, input: PoseInput): void {
  const phase = (input.travelled / 0.95) * Math.PI * 2;
  const sin = Math.sin(phase);
  const left = Math.max(0, sin);
  const right = Math.max(0, -sin);
  const pelvis = bone(B.pelvis);
  pelvis.position.set(0, 0.24, -0.45);
  // Flat on the ground, and snaking: hips swing one way, shoulders the other.
  pelvis.rotation.set(Math.PI / 2 - 0.04, 0, 0.16 * sin);
  bone(B.spine).rotation.set(-0.1, 0, -0.26 * sin);
  bone(B.head).rotation.set(-1.0, 0, 0.1 * sin);

  // Elbows ahead of the shoulders, forearms angled in over the rifle; one arm reaches
  // while the other pulls. Kept close to the body: wide arms read as a surrender, not a crawl.
  bone(B.lUpperArm).rotation.set(0, 0, 2.6 + 0.32 * sin);
  bone(B.lForeArm).rotation.set(0, 0, 0.85 - 0.2 * sin);
  bone(B.rUpperArm).rotation.set(0, 0, -(2.6 - 0.32 * sin));
  bone(B.rForeArm).rotation.set(0, 0, -(0.85 + 0.2 * sin));

  // Legs trail almost straight behind; the knee on the reaching side is drawn up a little
  // and out, then pushes back.
  bone(B.lThigh).rotation.set(0, 0, 0.1 + 0.5 * right);
  bone(B.lShin).rotation.set(0, 0, -0.85 * right);
  bone(B.rThigh).rotation.set(0, 0, -(0.1 + 0.5 * left));
  bone(B.rShin).rotation.set(0, 0, 0.85 * left);

  // Rifle cradled across the forearms, pointing where he is going.
  bone(B.weapon).position.set(-0.2, 0.62, 0.02);
  bone(B.weapon).rotation.set(-Math.PI / 2, 0, 0.12);
}

/** The corpse: on his back, head away from where he was facing. Every death ends exactly here. */
export function dead(bone: BoneOf): void {
  const pelvis = bone(B.pelvis);
  pelvis.position.set(0, 0.16, 0);
  pelvis.rotation.set(-Math.PI / 2 + 0.05, 0, 0.2);
  bone(B.lUpperArm).rotation.set(0.2, 0, -1.3);
  bone(B.rUpperArm).rotation.set(-0.4, 0, 0.8);
  bone(B.rForeArm).rotation.x = -0.9;
  bone(B.lThigh).rotation.set(0.1, 0, -0.3);
  bone(B.rThigh).rotation.set(-0.5, 0, 0.2);
  bone(B.rShin).rotation.x = 0.9;
  bone(B.head).rotation.y = 0.6;
  // The rifle lies where it fell, beside him.
  bone(B.weapon).position.set(-0.55, 0.3, -0.1);
  bone(B.weapon).rotation.set(Math.PI / 2, 0.9, 0);
}
